import { BrowserRouter, Routes, Route } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Navbar from "./components/Navbar.tsx";
import { API_BASE, setTokenGetter } from "./services/api.ts";
import Home from "./pages/Home.tsx";
import Admins from "./pages/admins/Admins.tsx";
import AdminDetails from "./pages/admins/AdminDetails.tsx";
import Conversations from "./pages/conversations/Conversations.tsx";
import ConversationDetails from "./pages/conversations/ConversationDetails.tsx";
import Categories from "./pages/categories/Categories.tsx";
import CategoryDetails from "./pages/categories/CategoryDetails.tsx";
import DocumentStatus from "./pages/categories/DocumentStatus.tsx";
import ConfigCode from "./pages/ConfigCode.tsx";
import styled from "styled-components";


const AppContainer = styled.div`
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  min-height: 100vh;
  color: #001f52;
  padding-top: 60px; /* give room for navbar */
  transition: background 0.3s ease, color 0.3s ease;

  .dark-mode & {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    color: #e0e0e0;
  }
`;

const LoginContainer = styled.div`
  min-height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  padding: 2rem;
  padding-top: 4rem;
  transition: background 0.3s ease, color 0.3s ease;

  .dark-mode & {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  }
`;

const LoginContent = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
`;

const MainCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 8px 24px rgba(0, 31, 82, 0.1);
  text-align: center;
  max-width: 800px;
  width: 100%;
  transition: all 0.3s ease;

  .dark-mode & {
    background: #2a2a3e;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const MainHeading = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  color: #001f52;
  margin: 0 0 1rem 0;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #001f52;
  margin: 0 0 1.5rem 0;
  line-height: 1.6;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #b0b0b0;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Description = styled.p`
  font-size: 1rem;
  color: #001f52;
  margin: 0 0 2rem 0;
  line-height: 1.6;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #b0b0b0;
  }
`;

const CtaCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 8px 24px rgba(0, 31, 82, 0.1);
  text-align: center;
  max-width: 600px;
  width: 100%;
  transition: all 0.3s ease;

  .dark-mode & {
    background: #2a2a3e;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const CtaHeading = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #001f52;
  margin: 0 0 0.5rem 0;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const CtaText = styled.p`
  font-size: 1rem;
  color: #001f52;
  margin: 0 0 1.5rem 0;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #b0b0b0;
  }
`;

const LoginButton = styled.button`
  background: #001f52;
  color: #f2f3f6;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 8px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 31, 82, 0.2);
  width: 100%;
  max-width: 300px;

  &:hover {
    background: #003080;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 31, 82, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  .dark-mode & {
    background: #64da4c;
    color: #001f52;
    box-shadow: 0 4px 6px rgba(100, 218, 76, 0.3);

    &:hover {
      background: #7ae66a;
      box-shadow: 0 6px 12px rgba(100, 218, 76, 0.4);
    }
  }
`;

enum BackendAuthStatus {
  Pending = "pending",
  Success = "success",
  Error = "error",
}

function App() {

  const { user, loginWithRedirect, getAccessTokenSilently, isLoading } = useAuth0();
  const [backendAuthStatus, setBackendAuthStatus] = useState<BackendAuthStatus>(BackendAuthStatus.Pending);

  useEffect(() => {
    setTokenGetter(() => getAccessTokenSilently());
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (user) {
      const fun = async () => {
        try {
          const token = await getAccessTokenSilently();

          if (!user.name || typeof user.name !== 'string') {
            throw new Error('User name is missing or invalid');
          }
          if (!user.email || typeof user.email !== 'string') {
            throw new Error('User email is missing or invalid');
          }

          const name = user.name;
          const email = user.email;

          const response = await fetch(`${API_BASE}/admins/me`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              name: name,
              email: email
            })
          });

          if (response.status === 200) {
            setBackendAuthStatus(BackendAuthStatus.Success);
          } else {
            throw new Error(`Backend auth failed with status ${response.status}`);
          }

        } catch (error) {
          console.error('Error authenticating with backend:', error);
          setBackendAuthStatus(BackendAuthStatus.Error);
        }
      };
      fun();
    } else {
      setBackendAuthStatus(BackendAuthStatus.Pending);
    }
  }, [user]);

  if ((user && backendAuthStatus === BackendAuthStatus.Pending) || isLoading) return <div>Vent venligst...</div>;

  if (backendAuthStatus === BackendAuthStatus.Error) return <div>Fejl ved login. Prøv igen.</div>;

  return (
    <BrowserRouter>
      <Navbar />

      {!user ? (
        <LoginContainer>
          <LoginContent>
            <MainCard>
              <MainHeading>Admin portal</MainHeading>
              <Subtitle>Nem og effektiv overblik over chats</Subtitle>
              <Description>Log ind for at administrere feedback</Description>
            </MainCard>

            <CtaCard>
              <CtaHeading>Klar til at komme i gang?</CtaHeading>
              <CtaText>Log ind nu og oplev hvor nemt det er at administrere dine feedbacks</CtaText>
              <LoginButton onClick={() => loginWithRedirect()}>
                Log ind nu
              </LoginButton>
            </CtaCard>

          </LoginContent>
        </LoginContainer>
      )
        : (<AppContainer>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/admins" element={<Admins />} />
            <Route path="/admins/:id" element={<AdminDetails />} />
            <Route path="/conversations" element={<Conversations />} />
            <Route path="/conversations/:id" element={<ConversationDetails />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/categories/document-status" element={<DocumentStatus />} />
            <Route
              path="/categories/:type/:id/hierarchy-nodes/:nodeId"
              element={<CategoryDetails />}
            />
            <Route path="/categories/:type/:id" element={<CategoryDetails />} />
            <Route path="/config" element={<ConfigCode />} />
          </Routes>
        </AppContainer>)


      }

    </BrowserRouter>
  );
}

export default App;
